import { useId, useMemo, useState } from "react"
import {
  useGetMetaAccountAssignmentsQuery,
  useGetMetaAccountUserInventoryQuery,
} from "~/ui-rtk/api/metaAccountApi"
import type {
  MetaAccountAssetHolder,
  MetaAccountUser,
} from "~/ui-rtk/api/types/meta-account.types"
// Direct imports (not the `common` / `controls` barrels) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import AccessChip from "./AccessChip"
import AssignMetaAccessDialog, {
  DIALOG_CONTAINER_CLS,
  type MetaAccessAssetIds,
} from "./AssignMetaAccessDialog"
import {
  ASSET_GROUP,
  ASSIGN_TO,
  BUTTON,
  COMPANIES_LOAD_ERROR,
  DISTRIBUTE_COLUMN,
  DISTRIBUTE_TAB_COMPANIES,
  INVENTORY_LOAD_FAILED,
  MANAGE_LOADING,
  NONE,
  NO_UNASSIGNED_ASSETS,
  NO_UNASSIGNED_COMPANIES,
  ONLY_UNASSIGNED_TOGGLE,
  distributeTitle,
  optionLabel,
  suggestionTextOf,
  unassignedAssetsTab,
} from "./copy"
import {
  assignmentStatusChipOf,
  suggestionsFor,
  unheldAssetCount,
  type CompanySuggestion,
} from "./meta-access.utils"

export interface DistributeDialogProps {
  credential: MetaAccountUser
  onClose: () => void
}

type Tab = "companies" | "assets"

interface AssignTarget {
  companyId: string
  initialAssets: Partial<MetaAccessAssetIds> | undefined
}

interface AssetRow {
  key: keyof MetaAccessAssetIds
  id: string
  label: string
  heldBy: MetaAccountAssetHolder | null
}

/**
 * Hand one login's reach out to companies (APP-566 T2). "Companies" lists
 * every live company with its assignment and a suggestion from this login's
 * unused assets; "Unassigned assets" lists what the login reaches that no
 * company uses yet. Either way, Assign opens the assign dialog on this login,
 * and this dialog steps aside until it closes.
 */
export default function DistributeDialog({
  credential,
  onClose,
}: DistributeDialogProps) {
  const fieldId = useId()
  const companiesQuery = useGetMetaAccountAssignmentsQuery()
  const inventoryQuery = useGetMetaAccountUserInventoryQuery({
    id: credential.id,
  })
  const [tab, setTab] = useState<Tab>("companies")
  const [onlyUnassigned, setOnlyUnassigned] = useState(true)
  const [assignFor, setAssignFor] = useState<AssignTarget | null>(null)
  /** Unassigned assets → the company picked for each row (`key-id`). */
  const [picked, setPicked] = useState<Record<string, string>>({})

  const companies = useMemo(
    () => companiesQuery.data?.companies ?? [],
    [companiesQuery.data],
  )
  const inventory = inventoryQuery.data ?? null
  const suggestions = useMemo(
    () =>
      inventory
        ? suggestionsFor(inventory, companies)
        : new Map<string, CompanySuggestion>(),
    [inventory, companies],
  )
  const shown = onlyUnassigned
    ? companies.filter(company => company.assignment === null)
    : companies
  const assignCompany = assignFor
    ? companies.find(company => company.companyId === assignFor.companyId)
    : undefined

  const assetGroups: Array<{ title: string; rows: AssetRow[] }> = inventory
    ? [
        {
          title: ASSET_GROUP.adAccounts,
          rows: inventory.adAccounts.map(account => ({
            key: "adAccountId" as const,
            id: account.id,
            label: optionLabel(`act_${account.id}`, account.name),
            heldBy: account.heldBy,
          })),
        },
        {
          title: ASSET_GROUP.instagramAccounts,
          rows: inventory.instagramAccounts.map(ig => ({
            key: "igUserId" as const,
            id: ig.id,
            label: optionLabel(ig.id, ig.username ? `@${ig.username}` : null),
            heldBy: ig.heldBy,
          })),
        },
        {
          title: ASSET_GROUP.pages,
          rows: inventory.pages.map(page => ({
            key: "pageId" as const,
            id: page.id,
            label: optionLabel(page.id, page.name),
            heldBy: page.heldBy,
          })),
        },
      ]
        .map(group => ({
          ...group,
          rows: group.rows.filter(asset => asset.heldBy === null),
        }))
        .filter(group => group.rows.length > 0)
    : []

  const companiesBody = (() => {
    if (companiesQuery.isLoading) return <p>{MANAGE_LOADING}</p>
    if (companiesQuery.isError) {
      return <p className="text-states-error">{COMPANIES_LOAD_ERROR}</p>
    }
    return (
      <>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={onlyUnassigned}
            onChange={({ target }) => setOnlyUnassigned(target.checked)}
          />
          {ONLY_UNASSIGNED_TOGGLE}
        </label>
        {shown.length === 0 ? (
          <p>{NO_UNASSIGNED_COMPANIES}</p>
        ) : (
          <table className="w-full text-left">
            <thead className="text-system-gray-400">
              <tr>
                <th className="py-1 pr-2 font-normal">
                  {DISTRIBUTE_COLUMN.company}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {DISTRIBUTE_COLUMN.assignment}
                </th>
                <th className="py-1 pr-2 font-normal">
                  {DISTRIBUTE_COLUMN.suggestion}
                </th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {shown.map(company => {
                const suggestion = suggestions.get(company.companyId)
                const chip = company.assignment
                  ? assignmentStatusChipOf(company.assignment)
                  : null
                return (
                  <tr
                    key={company.companyId}
                    className="border-t border-basic-blue"
                  >
                    <td className="py-1 pr-2 text-white">
                      {company.companyName}
                    </td>
                    <td className="py-1 pr-2">
                      {company.assignment && chip ? (
                        <span className="flex flex-wrap items-center gap-1">
                          <span>
                            {company.assignment.credentialName ?? NONE}
                          </span>
                          <AccessChip tone={chip.tone} title={chip.title}>
                            {chip.label}
                          </AccessChip>
                        </span>
                      ) : (
                        NONE
                      )}
                    </td>
                    <td className="py-1 pr-2">
                      {suggestionTextOf(suggestion)}
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() =>
                          setAssignFor({
                            companyId: company.companyId,
                            // A name match is only a guess: offer it, never on an existing assignment.
                            initialAssets:
                              !company.assignment && suggestion?.kind === "name"
                                ? { igUserId: suggestion.igUserId }
                                : undefined,
                          })
                        }
                      >
                        {company.assignment ? BUTTON.edit : BUTTON.assign}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </>
    )
  })()

  const assetsBody = (() => {
    if (inventoryQuery.isError) {
      return (
        <p className="text-states-error">
          {getServerErrorMessage(inventoryQuery.error, INVENTORY_LOAD_FAILED)}
        </p>
      )
    }
    if (!inventory) return <p>{MANAGE_LOADING}</p>
    if (assetGroups.length === 0) return <p>{NO_UNASSIGNED_ASSETS}</p>
    return assetGroups.map(group => (
      <section key={group.title} className="flex flex-col gap-1">
        <h3 className="font-medium text-white">{group.title}</h3>
        <ul className="flex flex-col gap-1">
          {group.rows.map(asset => {
            const rowKey = `${asset.key}-${asset.id}`
            const companyId = picked[rowKey] ?? ""
            return (
              <li
                key={rowKey}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <span>{asset.label}</span>
                <span className="flex items-center gap-2">
                  {/* Picking only selects: arrow keys and type-ahead change a closed select's value. */}
                  <Select
                    aria-label={`${asset.label}: ${ASSIGN_TO}`}
                    className="w-56"
                    options={[
                      { value: "", label: ASSIGN_TO, disabled: true },
                      ...companies.map(company => ({
                        value: company.companyId,
                        label: company.companyName,
                      })),
                    ]}
                    value={companyId}
                    onChange={({ target }) =>
                      setPicked(prev => ({ ...prev, [rowKey]: target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    aria-label={`${asset.label}: ${BUTTON.assign}`}
                    disabled={!companyId}
                    onClick={() =>
                      setAssignFor({
                        companyId,
                        initialAssets: { [asset.key]: asset.id },
                      })
                    }
                  >
                    {BUTTON.assign}
                  </button>
                </span>
              </li>
            )
          })}
        </ul>
      </section>
    ))
  })()

  return (
    <>
      <Dialog
        isOpen={!assignCompany}
        handleClose={onClose}
        closeOnClickOutside
        width={720}
        classes={{ container: DIALOG_CONTAINER_CLS }}
      >
        <h2 className="text-lg font-semibold text-white">
          {distributeTitle(credential.connectedUser?.name ?? null)}
        </h2>

        <div role="tablist" className="tabs tabs-bordered">
          {(
            [
              ["companies", DISTRIBUTE_TAB_COMPANIES],
              [
                "assets",
                unassignedAssetsTab(
                  inventory ? unheldAssetCount(inventory) : 0,
                ),
              ],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              id={`${fieldId}-${key}`}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`tab ${tab === key ? "tab-active" : ""}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          aria-labelledby={`${fieldId}-${tab}`}
          className="flex flex-col gap-2"
        >
          {tab === "companies" ? companiesBody : assetsBody}
        </div>

        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
          >
            {BUTTON.close}
          </button>
        </div>
      </Dialog>

      {assignFor && assignCompany && (
        // Opened on this login, whatever the company's assignment uses today.
        <AssignMetaAccessDialog
          companyId={assignCompany.companyId}
          companyName={assignCompany.companyName}
          connector={assignCompany.connector}
          assignment={assignCompany.assignment}
          initialCredentialId={credential.id}
          initialAssets={assignFor.initialAssets}
          onClose={() => setAssignFor(null)}
        />
      )}
    </>
  )
}
